import axios from "axios";
import url from "../constants/url";
import { Cclmodule, apiRoutes } from "../constants/routes";
import { TOKEN_EXPIRED_CODE, REFRESH_TOKEN_EXPIRED_CODE, UNAUTHORIZED_CODE, SESSION_EXPIRED_MESSAGE_KEY } from "../constants/autoLogout";
import { commonStrings } from "../components/StringBundle";

const instance = axios.create({
  baseURL: url.apiUrl,
  // The access/refresh tokens are HttpOnly cookies, not headers — the
  // browser needs this to send/accept them on cross-origin requests.
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    Accept: "*",
  },
});

// Request interceptor
instance.interceptors.request.use(
  (config) => {
    // Normalize empty values
    if (config.data) {
      for (let key of Object.keys(config.data)) {
        if (config.data[key] === "" || config.data[key] === undefined) {
          config.data[key] = null;
        }
      }
    }

    // Hook into AbortController before sending
    if (config.signal) {
      config.signal.addEventListener("abort", () => {
        // Cancel the request while it's in progress
        if (config.cancelToken) {
          config.cancelToken.cancel("Request aborted");
        }
      });
    }

    // If already aborted, reject immediately
    if (config.signal?.aborted) {
      return Promise.reject(new axios.CanceledError("Request aborted before sending"));
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
instance.interceptors.response.use(
  (response) => {
    // If aborted mid-flight, reject
    if (response.config?.signal?.aborted) {
      const cancelError = new axios.CanceledError("Request aborted");
      return Promise.reject(cancelError);
    }

    if (response.status >= 200 && response.status < 300) {
      return response.data;
    }
    return response;
  },
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const originalConfig = error.config;
    const code = error.response?.data?.code;
    // UNAUTHORIZED (cookie missing) is only refreshable outside skipAuthRedirect calls like OTP verify, where a 401 just means "wrong code".
    const isRefreshable = error.response?.status === 401 &&
      (code === TOKEN_EXPIRED_CODE || (code === UNAUTHORIZED_CODE && !originalConfig?.skipAuthRedirect));

    // Refreshable 401s retry once after a silent refresh; any other 401 goes straight back to login.
    if (!isRefreshable || originalConfig?._retriedAfterRefresh) {
      if (error.response?.status === 401 && !originalConfig?.skipAuthRedirect) {
        forceLogout();
      }
      return Promise.reject(error);
    }

    try {
      await refreshAccessTokenOnce();
      originalConfig._retriedAfterRefresh = true;
      return instance(originalConfig);
    } catch (refreshError) {
      // Only a confirmed dead refresh token ends the session — a transient failure must not log the user out.
      const isRefreshTokenExpired = refreshError.response?.data?.code === REFRESH_TOKEN_EXPIRED_CODE;
      if (isRefreshTokenExpired && !originalConfig?.skipAuthRedirect) {
        forceLogout();
      }
      return Promise.reject(refreshError);
    }
  }
);

// Coalesces concurrent 401s into one shared refresh call — also reused by AppBar.js's proactive timer.
let refreshInFlight = null;
export function refreshAccessTokenOnce() {
  if (!refreshInFlight) {
    // Plain axios, not `instance` — this must not go through the interceptor
    // above (a failed refresh has nothing left to refresh with).
    refreshInFlight = axios
      .post(`${url.packagingapiurl}${Cclmodule.auth}/${apiRoutes.refresh}`, null, { withCredentials: true })
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

// Logs out every device (Cognito GlobalSignOut) — not called yet, kept for a future "log out of all devices" option.
let logoutInFlight = null;
export function logoutOnce() {
  if (!logoutInFlight) {
    // Plain axios, not `instance` — a failed logout call must not recurse back through this interceptor.
    logoutInFlight = axios.post(`${url.packagingapiurl}${Cclmodule.auth}/${apiRoutes.logout}`, null, { withCredentials: true });
  }
  return logoutInFlight;
}

// Same coalescing as logoutOnce, but hits /logout-device — only revokes this browser's session.
let logoutDeviceInFlight = null;
export function logoutDeviceOnce() {
  if (!logoutDeviceInFlight) {
    logoutDeviceInFlight = axios.post(`${url.packagingapiurl}${Cclmodule.auth}/${apiRoutes.logoutDevice}`, null, { withCredentials: true });
  }
  return logoutDeviceInFlight;
}

// messageKey picks which message Login.tsx shows — index.js's stale-session check passes "autoLogoutMessage" (inactivity) instead of "session expired".
export async function forceLogout(messageKey = SESSION_EXPIRED_MESSAGE_KEY) {
  try {
    await logoutDeviceOnce();
  } catch (err) {
    console.log(commonStrings.logoutRequestFailed, err);
  }
  localStorage.clear();
  sessionStorage.clear();
  sessionStorage.setItem(messageKey, "1"); // set after clear() so it survives the reload
  window.location.pathname = "/login";
}

export default instance;
